<template>
	<div class="sideways-content-fold" v-if="activePanel">
		<div class="portlet light bordered">
			<div class="portlet-title">
				<div class="caption">
					<template v-if="process === 'step_1'">
						Edit Contest Prizes
					</template>
					<template v-if="process === 'step_2'">
						{{ activePrize.id ? 'Edit' : 'Create New' }} Contest Prize
					</template>
					<template v-if="process === 'step_3'">
						Create New Contest Rule
					</template>
				</div>
			</div>
			<div class="portlet-body">
				<div class="alert alert-danger" v-if="errorMessage.length">
				    <button class="close" data-close="alert" @click="clearError()"></button>
                    <span>{{ errorMessage }}</span>
				</div>
				<transition name="fade" mode="out-in">
					<div v-if="process === 'step_1'" key="step_1">
						<button type="button" class="btn blue btn-lg" @click="throttledCreate()">Create a New Prize</button>
						<div class="prize-progress">
							<template v-for="prize in prizes">
								<div class="clickable" :style="{backgroundColor: prize.color, width: `${prize.weight}%`, padding: `2px ${ (prize.weight >= 2) ? '5px' : '0'}`}" @mouseover="selectPrize(prize)">{{ (prize.weight >= 4) ? `${prize.weight}%` : '' }}</div>
							</template>
						</div>
						<i class="font-red-thunderbird" v-if="prizes.length && currentCoverage < 100"><small>* Note that 100% prize coverage is required to run your campaign.</small></i>
						<div v-if="activePrize.id" class="portlet box blue-hoki margin-top-20" :style="{border: `1px solid ${activePrize.color}`}">
							<div class="portlet-title" :style="{backgroundColor: activePrize.color}">
								<div class="caption">
									{{ activePrize.name }}
								</div>
							</div>
							<div class="portlet-body">
								<div>
									<img :src="activePrize.image" class="active-prize-image">
								</div>
								<div class="margin-top-20">
									<ul style="listStyleType: none; paddingLeft: 0">
										<li>
											<b>Quantity: </b>{{ (activePrize.quantity === 0) ? 'Unlimited' : activePrize.quantity }}
										</li>
										<li>
											<b>Odds of Winning: </b>{{ activePrize.weight }}%
										</li>
										<li>
											<b>Claim Type: </b>{{ activePrize.claim_type }}
										</li>
									</ul>
									<button type="button" class="btn blue btn-sm margin-top-20" @click="editPrize(activePrize)">Edit Prize</button>
									<button type="button" class="btn blue btn-sm margin-top-20" @click="addRule()">Add Prize Rule</button>
									<button type="button" class="btn blue btn-sm margin-top-20" @click="viewRules(activePrize)">View Prize Rules</button>
								</div>
							</div>
						</div>
						<div v-if="!prizes.length" class="center margin-top-20 font-grey-cascade">
							<i>No prizes created yet.</i>
						</div>
					</div>
					<div v-if="process === 'step_2'" key="step_2">
						<form role="form" @submit.prevent="createContestPrize($event)" novalidate>
							<div class="form-group form-md-line-input form-md-floating-label margin-bottom-40">
	                            <input type="text" class="form-control input-sm" :class="{'edited': newPrize.name.length}" id="new_prize_name" v-model.trim="newPrize.name" maxlength="255">
	                            <label for="new_prize_name">Name</label>
	                            <span class="help-block persist">This is the name of the prize that will be displayed to the user.</span>
	                        </div>
	                        <div class="form-group form-md-line-input form-md-floating-label margin-bottom-40">
	                            <input type="text" class="form-control input-sm" :class="{'edited': newPrize.image.length}" id="new_prize_image" v-model.trim="newPrize.image" maxlength="255">
	                            <label for="new_prize_image">Image</label>
	                            <span class="help-block persist">This is the image of the prize that will be displayed to the user.</span>
	                        </div>
	                        <div class="form-group form-md-line-input form-md-floating-label">
	                            <input type="number" class="form-control input-sm" :class="{'edited': newPrize.quantity || newPrize.quantity === 0}" id="new_prize_quantity" v-model.trim="newPrize.quantity" maxlength="255" :disabled="newPrize.unlimited_quantity">
	                            <label for="new_prize_quantity">Quantity</label>
	                            <span class="help-block persist">The total quantity of this prize that will be available.</span>
	                        </div>
	                        <div class="form-md-checkboxes margin-bottom-20">
	            				<div class="md-checkbox-inline">
	            					<div class="md-checkbox">
	            						<input type="checkbox" id="checkbox-no-age-limit" class="md-check" v-model="newPrize.unlimited_quantity">
	            						<label for="checkbox-no-age-limit">
	            							<span></span>
	            							<span class="check"></span>
	            							<span class="box"></span>
	            							Unlimited Quantity
	            						</label>
	            						<info-popover :content="'Enabling this will set the limit of this prize to unlimited.'"></info-popover>
	            					</div>
	            				</div>
	            			</div>
	            			<div class="form-group form-md-line-input form-md-floating-label margin-bottom-40">
	                            <input type="number" class="form-control input-sm" :class="{'edited': newPrize.weight > 0}" id="new_prize_weight" v-model.trim="newPrize.weight" min="0" max="100">
	                            <label for="new_prize_weight">Odds of Winning (%)</label>
	                            <span class="help-block persist">This value represents the odds (out of 100) that a user has of winning this prize.</span>
	                        </div>
	                        <div class="margin-bottom-20">
								<label class="dropdown-label-override">
									Claim Type
									<info-popover :content="'Select `none` if this is a prize that cannot be claimed.'"></info-popover>
								</label>
								<dropdown>
									<button slot="button" type="button" class="btn btn-sm btn-outline dropdown-toggle btn-default" data-toggle="dropdown">
										{{ newPrize.claim_type || 'Select a Claim Type' }}
										<i class="fa fa-angle-down"></i>
									</button>
									<ul slot="dropdown-menu" class="dropdown-menu">
										<li>
											<a @click="setClaimType('none')">none</a>
										</li>
										<li>
											<a @click="setClaimType('text')">text</a>
										</li>
										<li>
											<a @click="setClaimType('href')">href</a>
										</li>
										<li>
											<a @click="setClaimType('post_request')">post_request</a>
										</li>
									</ul>
								</dropdown>
							</div>
							<div class="form-group form-md-line-input form-md-floating-label margin-bottom-40" v-if="newPrize.claim_type.length && newPrize.claim_type !== 'none'">
	                            <input type="text" class="form-control input-sm" :class="{'edited': newPrize.claim.length}" id="new_prize_claim" v-model.trim="newPrize.claim">
	                            <label for="new_prize_claim">
	                            	<template v-if="newPrize.claim_type === 'text'">
	                            		Claim Text
	                            	</template>
	                            	<template v-if="newPrize.claim_type === 'href'">
	                            		Claim Link
	                            	</template>
	                            	<template v-if="newPrize.claim_type === 'post_request'">
	                            		Claim Post Request
	                            	</template>
	                            </label>
	                        </div>
	                        <div class="form-group form-md-line-input form-md-floating-label margin-bottom-40" v-if="newPrize.claim_type.length && newPrize.claim_type === 'post_request'">
	                            <input type="text" class="form-control input-sm" :class="{'edited': newPrize.claim_post_request_params.length}" id="new_prize_claim" v-model.trim="newPrize.claim_post_request_params">
	                            <label for="new_prize_claim">
	                            	Post Request Claim Type
	                            </label>
	                        </div>
							<div class="form-actions noborder invite-user-actions margin-top-20">
	                        	<button type="button" class="btn btn-default exit-invitation-setup" @click="closePanel()"> Return To UNOgames Dashboard</button>
	                        	<button type="button" class="btn btn-default" @click="reverseForm()">Back</button>
	                        	<button type="submit" class="btn blue" key="add-contest-rules-submit">Submit <i class="fa fa-arrow-circle-right"></i></button>
	                        </div>
	                    </form>
					</div>
					<div v-if="process === 'step_3'" key="step_3">
						<form role="form" @submit.prevent="createPrizeRule($event)" novalidate>
							<div class="form-group form-md-line-input form-md-floating-label no-top-padding">
								<label class="dropdown-label-override">Starts At</label>
								<el-time-picker v-model="newRule.starts_at" format="h:mm A" :editable="false" placeholder="Select Start Time"></el-time-picker>
							</div>
							<div class="form-group form-md-line-input form-md-floating-label no-top-padding">
								<label class="dropdown-label-override">Ends At</label>
								<el-time-picker v-model="newRule.ends_at" format="h:mm A" :editable="false" placeholder="Select End Time"></el-time-picker>
							</div>
							<div class="form-md-checkboxes margin-bottom-20">
                				<div class="md-checkbox-inline">
                					<div class="md-checkbox">
                						<input type="checkbox" id="checkbox-specific-day" class="md-check" v-model="newRule.specific_day" :disabled="process !== 'step_3'">
                						<label for="checkbox-specific-day">
                							<span></span>
                							<span class="check"></span>
                							<span class="box"></span>
                							Only on specific day
                						</label>
                						<info-popover :content="'By enabling this, the promotional odds will only run on one specific day.'"></info-popover>
                					</div>
                				</div>
                			</div>
							<div class="form-group form-md-line-input form-md-floating-label no-top-padding" v-if="newRule.specific_day">
								<label class="dropdown-label-override">Date</label>
								<el-date-picker v-model="newRule.date" :format="'MMM dd, yyyy'" type="date" :editable="false" placeholder="Select Date"></el-date-picker>
							</div>
							<div class="form-group form-md-line-input form-md-floating-label margin-bottom-40">
								<input type="number" class="form-control input-sm" v-model="newRule.max_redemptions" :class="{'edited': newRule.max_redemptions >= 0}" min="0">
								<label>Max Redemptions</label>
								<span class="help-block persist">The maximum redemptions that will be available for this prize. Use ‘0’ if unlimited.</span>
							</div>
							<div class="form-group form-md-line-input form-md-floating-label margin-bottom-40">
								<input type="number" class="form-control input-sm" v-model="newRule.weight" :class="{'edited': newRule.weight > 0}" min="0" max="100">
								<label>Odds of Winning (%)</label>
								<span class="help-block persist">This value represents the odds (out of 100) that a user has of winning this prize.</span>
							</div>
							<div class="form-actions noborder invite-user-actions margin-top-20">
	                        	<button type="button" class="btn btn-default exit-invitation-setup" @click="closePanel()"> Return To UNOgames Dashboard</button>
	                        	<button type="button" class="btn btn-default" @click="reverseForm()">Back</button>
	                        	<button type="submit" class="btn blue" key="edit-contest-prize-submit">Submit <i class="fa fa-arrow-circle-right"></i></button>
	                        </div>
	                    </form>
					</div>
				</transition>
			</div>
		</div>
	</div>
</template>
<script>
import GlobalFunctions from '../../../global'
import Dropdown from '../../modules/Dropdown'
import InfoPopover from '../../modules/InfoPopover'
import { throttle } from 'lodash'
import UNOgames from '../../../controllers/UNOgames'
import { mapGetters } from 'vuex'

/**
 * Edit Contest Prizes module is a page for editing the prizes of the contest.
 * @class EditContestPrizes
 * @extends UNOgames
 * @since 0.0.9
 */

const colors = ['#4b77be', '#1bbc9b', '#5e738b', '#29b4b6', '#e7505a', '#d91e18', '#f3c200', '#e87e04', '#8e44ad', '#796799']

export default {
	name: 'edit-contest-prizes',
	data () {
		return {
			process: 'step_1',
			activePanel: false,
			errorMessage: '',
			campaignId: 0,
			newPrize: {
				name: '',
				image: '',
				quantity: '',
				unlimited_quantity: false,
				weight: '',
				claim_type: '',
				claim: '',
				claim_post_request_params: ''
			},
			newRule: {
				starts_at: '',
				ends_at: '',
				specific_day: false,
				date: '',
				weight: '',
				max_redemptions: 0
			},
			steps: [{key: 'step_1', name: 'View Prizes'}, {key: 'step_2', name: 'Create Prize'}, {key: 'step_3', name: 'Add Prize Rule'}],
			prizes: [],
			unusedColors: [],
			activePrize: {},
			currentCoverage: 0,
			editingPrizeId: 0
		}
	},
	created () {
		if (this.$route.name === 'UNOgames - Edit Contest Prizes') {
			this.activePanel = true
			if (this.$route.params.campaign_id && this.$route.params.campaign_id !== 'undefined' && parseInt(this.$route.params.campaign_id) !== 0) {
				this.campaignId = this.$route.params.campaign_id
				this.getPrizes()
			} else {
				this.closePanel()
			}
		} else {
			this.activePanel = false
		}

		this.throttledCreate = throttle(() => {
			this.createPrize()
		}, 500, { 'trailing': false })
	},
	computed: mapGetters([
		'activeLocationId'
	]),
	methods: {
		/**
		 * To clear the current error.
		 * @function
		 * @returns {undefined}
		 * @memberof EditContestPrizes
		 * @version 0.0.9
		 */
		clearError () {
			this.errorMessage = ''
		},
		/**
		 * To close the edit contest prizes panel by redirecting to the dashboard.
		 * @function
		 * @returns {undefined}
		 * @memberof EditContestPrizes
		 * @version 0.0.9
		 */
		closePanel () {
			this.process = 'step_1'
			this.$router.push('/app/uno_games')
		},
		/**
		 * To move the form back to the previous step.
		 * @function
		 * @returns {undefined}
		 * @memberof EditContestPrizes
		 * @version 0.0.9
		 */
		reverseForm () {
			this.process = 'step_1'

			this.clearForm(true)

			return
		},
		/**
		 * To move to the create prize form in order to create the prize.
		 * @function
		 * @returns {undefined}
		 * @memberof EditContestPrizes
		 * @version 0.0.9
		 */
		createPrize () {
			if (this.currentCoverage === 100) {
				this.$swal({
					html: 'You can\'t create another prize as you already have 100% prize coverage?',
					type: 'info',
					confirmButtonText: 'Ok',
					showLoaderOnConfirm: true
				}).then(response => {
					// Do nothing on success
				}, dismiss => {
					// Do nothing on dismiss
				})
			} else {
				this.activePrize = Object.assign({})
				this.process = 'step_2'
			}
		},
		/**
		 * To validate that all information on the current step is accurate before allowing the user to proceed.
		 * @function
		 * @param {string} step - The step to be tested.
		 * @returns {object} A promise that will either return an error message or display the success screen.
		 * @memberof EditContestPrizes
		 * @version 0.0.9
		 */
		validateFormData (step) {
			let _this = this

			return new Promise((resolve, reject) => {
				switch (step) {
				case 'step_2':
					if (_this.newPrize.name.length < 3) {
						reject('Prize name must be at least 3 characters')
					}
					if (!_this.newPrize.image.length) {
						reject('Prize image cannot be blank. I don\'t know why, but it can\'t')
					}
					if (!_this.newPrize.quantity && !_this.newPrize.unlimited_quantity) {
						reject('Prize quantity cannot be blank.')
					}
					if (_this.newPrize.quantity < 1 && !_this.newPrize.unlimited_quantity) {
						reject('Prize quantity cannot be less than 1.')
					}
					if (!_this.newPrize.weight) {
						reject('Prize must have an odds of winning.')
					}
					if (_this.newPrize.weight > 100) {
						reject('Odds of winning a prize cannot be more than 100%.')
					}
					if (100 - _this.currentCoverage < _this.newPrize.weight) {
						reject(`Odds of winning all prizes cannot be more than 100%. Reduce odds of winning to be less than or equal to ${100 - _this.currentCoverage}%`)
					}
					if (!_this.newPrize.claim_type.length) {
						reject('You must choose a claim type.')
					}
					if (_this.newPrize.claim_type.length && _this.newPrize.claim_type !== 'none') {
						if (!_this.newPrize.claim.length) {
							reject('Claim details cannot be blank.')
						}
					}
					break

				case 'step_3':
					if (!(_this.newRule.starts_at instanceof Date)) {
						reject('Start time is required.')
					}
					if (!(_this.newRule.ends_at instanceof Date)) {
						reject('End time is required.')
					}
					if (!_this.newRule.max_redemptions && _this.newRule.max_redemptions !== 0) {
						reject('Max Redemptions cannot be blank.')
					}
					if (_this.newRule.max_redemptions < 1) {
						reject('Max Redemptions must be at least 1.')
					}
					if (!_this.newRule.weight) {
						reject('Rule must have an odds of winning.')
					}
					if (_this.newRule.weight > 100) {
						reject('Odds of winning a prize cannot be more than 100%.')
					}
					break

				default:
					resolve()
				}

				resolve()
			})
		},
		/**
		 * To pass the contest prize details to the backend to create the new prize.
		 * @function
		 * @param {object} event - The click event that prompted this function.
		 * @returns {undefined}
		 * @memberof EditContestPrizes
		 * @version 0.0.9
		 */
		createContestPrize (event) {
			let disabledButton = GlobalFunctions.disableButton(event)
			let _this = this

			return _this.validateFormData('step_2').then(response => {
				_this.clearError()

				if (_this.newPrize.unlimited_quantity) {
					_this.newPrize.quantity = 0
				}

				if (_this.newPrize.claim_type !== 'post_request') {
					delete _this.newPrize.claim_post_request_params
				}

				if (this.editingPrizeId) {
					UNOgames.updateContestPrize(_this.activeLocationId, _this.campaignId, _this.editingPrizeId, _this.newPrize).then(response => {
						_this.getPrizes()
						_this.process = 'step_1'
						_this.clearForm()
						_this.editingPrizeId = 0
						disabledButton.complete()
					}).catch(_this.$root.errorWrapper(e => {
						if (e.responseJSON) {
							switch (e.responseJSON.declaration) {}
						}
						disabledButton.cancel()
					}))
				} else {
					UNOgames.createContestPrize(_this.activeLocationId, _this.campaignId, _this.newPrize).then(response => {
						_this.getPrizes()
						_this.process = 'step_1'
						_this.clearForm()
						disabledButton.complete()
					}).catch(_this.$root.errorWrapper(e => {
						if (e.responseJSON) {
							switch (e.responseJSON.declaration) {}
						}
						disabledButton.cancel()
					}))
				}
			}).catch(reason => {
				// If validation fails then display the error message
				_this.errorMessage = reason
				disabledButton.cancel()
			})
		},
		/**
		 * To set the claim type of the new prize.
		 * @function
		 * @param {string} type - The claim type to be set.
		 * @returns {undefined}
		 * @memberof EditContestPrizes
		 * @version 0.0.9
		 */
		setClaimType (type) {
			this.newPrize.claim_type = type
		},
		/**
		 * To clear the object 'newPrize'.
		 * @function
		 * @param {boolean} keepPrizes - Determines whether or not to clear the prizes list.
		 * @returns {undefined}
		 * @memberof EditContestPrizes
		 * @version 0.0.9
		 */
		clearForm (keepPrizes) {
			if (!keepPrizes) {
				this.prizes = []
				this.activePrize = {}
			}
			this.newPrize = {
				name: '',
				image: '',
				quantity: '',
				unlimited_quantity: false,
				weight: '',
				claim_type: '',
				claim: '',
				claim_post_request_params: ''
			}
			this.newRule = {
				starts_at: '',
				ends_at: '',
				specific_day: false,
				date: '',
				weight: '',
				max_redemptions: 0
			}
		},
		/**
		 * To pull in all existing prizes for the current campaign.
		 * @function
		 * @returns {undefined}
		 * @memberof EditContestPrizes
		 * @version 0.0.9
		 */
		getPrizes () {
			let _this = this

			UNOgames.getPrizes(_this.activeLocationId, _this.campaignId).then(response => {
				_this.unusedColors = Object.assign([], colors)
				_this.prizes = response.payload.prize.sort((a, b) => {
					return b.weight - a.weight
				})
				_this.currentCoverage = 0
				_this.prizes.forEach(prize => {
					prize.color = _this.generateColor()
					_this.currentCoverage += prize.weight
				})
			}).catch(_this.$root.errorWrapper(e => {
				if (e.responseJSON) {
					switch (e.responseJSON.declaration) {
					case 'prizes_not_found':
						_this.prizes = []
						break

					default:
						// No default
					}
				}
			}))
		},
		/**
		 * To select a color from the remaining color list.
		 * @function
		 * @returns {string} - Returns the selected color string.
		 * @memberof EditContestPrizes
		 * @version 0.0.9
		 */
		generateColor () {
			let index = Math.floor((Math.random() * this.unusedColors.length - 1) + 1)
			let selectedColor = this.unusedColors[index]
			this.unusedColors.splice(index, 1)

			return selectedColor
		},
		/**
		 * To set the active prize to be displayed.
		 * @function
		 * @param {object} prize - The prize to be selected.
		 * @returns {undefined}
		 * @memberof EditContestPrizes
		 * @version 0.0.9
		 */
		selectPrize (prize) {
			if (this.activePrize.id !== prize.id) {
				this.activePrize = prize
			}
		},
		/**
		 * To select a prize to be edited.
		 * @function
		 * @param {object} prize - The prize to be edited.
		 * @returns {undefined}
		 * @memberof EditContestPrizes
		 * @version 0.0.9
		 */
		editPrize (prize) {
			this.process = 'step_2'
			this.editingPrizeId = prize.id
			this.newPrize = prize

			if (this.newPrize.quantity === 0) {
				this.newPrize.unlimited_quantity = true
			}
		},
		/**
		 * To move to the create prize rule form in order to create the prize rule.
		 * @function
		 * @returns {undefined}
		 * @memberof EditContestPrizes
		 * @version 0.0.9
		 */
		addRule () {
			this.process = 'step_3'
		},
		/**
		 * To pass the contest prize rule details to the backend to create the new prize rule.
		 * @function
		 * @param {object} event - The click event that prompted this function.
		 * @returns {undefined}
		 * @memberof EditContestPrizes
		 * @version 0.0.9
		 */
		createPrizeRule (event) {
			let disabledButton = GlobalFunctions.disableButton(event)
			let _this = this

			return _this.validateFormData('step_3').then(response => {
				_this.clearError()

				UNOgames.createContestPrizeRule(_this.activeLocationId, _this.campaignId, _this.activePrize.id, _this.newRule).then(response => {
					_this.getPrizes()
					_this.process = 'step_1'
					_this.clearForm()
					disabledButton.complete()
				}).catch(_this.$root.errorWrapper(e => {
					if (e.responseJSON) {
						switch (e.responseJSON.declaration) {}
					}
					disabledButton.cancel()
				}))
			}).catch(reason => {
				// If validation fails then display the error message
				_this.errorMessage = reason
				disabledButton.cancel()
			})
		}
	},
	components: {
		Dropdown,
		InfoPopover
	},
	watch: {
		/**
		 * To `watch` the $route in order to determine if this panel should be showing or not
		 * @function
		 * @returns {undefined}
		 * @memberof EditContestPrizes
		 * @version 0.0.9
		 */
		'$route' () {
			if (this.$route.name === 'UNOgames - Edit Contest Prizes') {
				this.activePanel = true
				this.clearError()
				this.clearForm()
				if (this.$route.params.campaign_id && this.$route.params.campaign_id !== 'undefined' && parseInt(this.$route.params.campaign_id) !== 0) {
					this.campaignId = this.$route.params.campaign_id
					this.getPrizes()
				} else {
					this.closePanel()
				}
			} else {
				this.activePanel = false
			}
		}
	}
}
</script>
<style>
.prize-progress {
	background-color: #e9edef;
	margin-top: 25px;
    height: 20px;
}
.prize-progress > div {
	display: inline-block;
	float: left;
	height: 20px;
	font-size: 11px;
	color: #fff;
}
.active-prize-image {
	width: 100%;
	margin: 0 auto;
	display: block;
	max-width: 250px;
}
</style>



// WEBPACK FOOTER //
// EditContestPrizes.vue?1e330e01